import type { QueryClient } from '@tanstack/react-query'
import type { TExhibitorWithEntries, UpdateShowExhibitorOutput } from '@schemas'

export type ShowEntrantClubSelectionInput = {
  clubId: string | null | undefined
  clubName: string | null | undefined
}

export type ShowEntrantClubSelection = {
  clubId: string | null
  clubName: string | null
}

export type ShowEntrantClubUpdatePayload = {
  clubId?: string | null
  clubName?: string | null
}

export function normalizeShowEntrantClubSelection(
  selection: ShowEntrantClubSelectionInput
): ShowEntrantClubSelection {
  const normalizedClubId = selection.clubId ?? null
  const rawClubName = typeof selection.clubName === 'string' ? selection.clubName.trim() : ''
  const normalizedClubName =
    normalizedClubId !== null ? null : rawClubName.length > 0 ? rawClubName : null

  return {
    clubId: normalizedClubId,
    clubName: normalizedClubName,
  }
}

export function buildShowEntrantClubUpdatePayload({
  next,
  current,
}: {
  next: ShowEntrantClubSelection
  current: ShowEntrantClubSelection
}): ShowEntrantClubUpdatePayload | null {
  const nextClubId = next.clubId ?? null
  const nextClubName = next.clubName ?? null
  const currentClubId = current.clubId ?? null
  const currentClubName = current.clubName ?? null

  if (nextClubId) {
    if (currentClubId === nextClubId && currentClubName === null) {
      return null
    }
    return { clubId: nextClubId, clubName: null }
  }

  if (nextClubName) {
    if (currentClubId === null && currentClubName === nextClubName) {
      return null
    }
    return { clubName: nextClubName }
  }

  if (currentClubId === null && currentClubName === null) {
    return null
  }

  return { clubId: null, clubName: null }
}

export function mergeShowEntrantIntoExhibitor(
  exhibitor: TExhibitorWithEntries,
  showEntrant: UpdateShowExhibitorOutput['showEntrant']
): TExhibitorWithEntries {
  const next: TExhibitorWithEntries = {
    ...exhibitor,
    grandChampionEligible: showEntrant.grandChampionEligible,
    heroAwardEligible: showEntrant.heroAwardEligible ?? exhibitor.heroAwardEligible ?? false,
    finalizedAt: showEntrant.finalizedAt ?? null,
    finalizedBy: showEntrant.finalizedBy ?? null,
    finalizedByRole: showEntrant.finalizedByRole ?? null,
    showClubId: showEntrant.clubId ?? null,
    showClubName: showEntrant.clubName ?? null,
    lastKnownClubId:
      showEntrant.clubId ?? (showEntrant.clubName ? (exhibitor.lastKnownClubId ?? null) : null),
    lastKnownClubName:
      showEntrant.clubName ?? (showEntrant.clubId ? (exhibitor.lastKnownClubName ?? null) : null),
  }

  if (showEntrant.clubId === null && showEntrant.clubName === null) {
    next.lastKnownClubId = null
    next.lastKnownClubName = null
  }

  return next
}

export function updateExhibitorCacheAfterShowEntrant({
  queryClient,
  showId,
  exhibitorId,
  showEntrant,
}: {
  queryClient: QueryClient | null | undefined
  showId: string | null | undefined
  exhibitorId: string
  showEntrant: UpdateShowExhibitorOutput['showEntrant']
}): void {
  if (!queryClient || !showId) {
    return
  }

  queryClient.setQueriesData<TExhibitorWithEntries[]>(
    {
      predicate: query => {
        const key = query.queryKey
        if (!Array.isArray(key) || key[0] !== 'exhibitors') return false
        const params = key[1]
        return (
          params &&
          typeof params === 'object' &&
          (params as { showId?: string | null }).showId === showId
        )
      },
    },
    current => {
      if (!current || !Array.isArray(current)) return current
      return current.map(item =>
        item.id === exhibitorId ? mergeShowEntrantIntoExhibitor(item, showEntrant) : item
      )
    }
  )
}
